import { Avatar, Progress, Space, Table, Tag, Typography } from 'antd';
import React from 'react';
import { useGetStudentsListQuery } from '../../../features/courses/coursesApi';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const attentionColors = {
    no_submission: 'red',
    pending_review: 'gold',
    low_grade: 'volcano',
};

const formatGrade = (grade) => {
    if (grade === null || grade === undefined) {
        return '-';
    }
    return Number(grade).toFixed(1);
};

const formatDate = (date) => {
    if (!date) {
        return '-';
    }
    return new Intl.DateTimeFormat('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(new Date(date));
};

// Per-course students table. Stats are scoped to `courseId` by the backend
// (StudentListApi ?course_id=...), so nothing is mixed across courses.
const CourseStudentsTable = ({ courseId }) => {
    const { t } = useTranslation();
    const { data: students, isLoading } = useGetStudentsListQuery(courseId, {
        skip: !courseId,
    });
    const columns = [
        {
            key: 'student',
            title: t('users.students.list.student'),
            render: (data) => (
                <Space direction="horizontal">
                    <Avatar
                        size={'large'}
                        icon={<UserOutlined />}
                        src={data?.profile_picture}
                    />
                    <Space direction="vertical" size={0}>
                        <Text strong>{data.username}</Text>
                        <Text type="secondary">{data.email}</Text>
                    </Space>
                </Space>
            ),
        },
        {
            key: 'progress',
            title: t('users.students.list.progress'),
            render: (data) => {
                const stats = data?.stats || {};
                return (
                    <Space direction="vertical" size={0} style={{ minWidth: 150 }}>
                        <Progress
                            percent={stats.progress_percent || 0}
                            size="small"
                        />
                        <Text type="secondary">
                            {stats.completed_lessons || 0}/{stats.total_lessons || 0}
                        </Text>
                    </Space>
                );
            },
        },
        {
            key: 'submissions',
            title: t('users.students.list.submissions'),
            render: (data) => {
                const stats = data?.stats || {};
                return (
                    <Space direction="vertical" size={0}>
                        <Text>{stats.submissions_count || 0}</Text>
                        {stats.pending_submissions_count ? (
                            <Tag color="gold">
                                {t('users.students.list.pendingSubmissions', {
                                    count: stats.pending_submissions_count,
                                })}
                            </Tag>
                        ) : null}
                    </Space>
                );
            },
        },
        {
            key: 'grade',
            title: t('users.students.list.grade'),
            render: (data) => {
                const stats = data?.stats || {};
                return (
                    <Space direction="vertical" size={0}>
                        <Text>{formatGrade(stats.latest_grade)}</Text>
                        {stats.average_grade !== null &&
                        stats.average_grade !== undefined ? (
                            <Text type="secondary">
                                {t('users.students.list.averageGrade', {
                                    grade: formatGrade(stats.average_grade),
                                })}
                            </Text>
                        ) : null}
                    </Space>
                );
            },
        },
        {
            key: 'last_activity_at',
            title: t('users.students.list.lastActivity'),
            render: (data) => formatDate(data?.stats?.last_activity_at),
        },
        {
            key: 'attention',
            title: t('users.students.list.needsAttention'),
            render: (data) => {
                const attention = data?.stats?.attention || [];
                if (!attention.length) {
                    return <Text type="secondary">-</Text>;
                }
                return (
                    <Space size={[0, 4]} wrap>
                        {attention.map((item) => (
                            <Tag key={item} color={attentionColors[item] || 'default'}>
                                {t(`users.students.list.attention.${item}`)}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
    ];
    return (
        <Table
            columns={columns}
            dataSource={students}
            loading={isLoading}
            locale={{ emptyText: t('users.students.list.empty') }}
            rowKey={'uuid'}
        />
    );
};

export default CourseStudentsTable;
